<template>
  <div :class="$style.page">
    <section :class="$style.page__section">
      <header :class="$style.header">
        <SectionTitle :class="$style.header__title">
          開催セミナー一覧
        </SectionTitle>
      </header>
      <section
        v-if="seminars && seminars.length > 0"
        :class="[$style.section__contents, $style.seminars]"
      >
        <AppAccordion
          :class="$style.seminars__item"
          v-for="seminar in seminars"
          :key="seminar.id"
        >
          <template #title>
            <p>{{ seminar.title }}</p>
          </template>
          <template #content="slot">
            <ExternalLink
              :href="buildShadowSeminarURL(seminar.id)"
              :class="$style.link"
              >このセミナーのページに移動</ExternalLink
            >
            <SeminarCalendar
              v-if="slot.isShown"
              :class="$style.seminars__calendar"
              :schedules="seminar.schedules"
              :title="seminar.title"
              @add="date => addSchedule(seminar.id, date)"
              @select="scheduleId => editSchedule(seminar.id, scheduleId)"
            />
          </template>
        </AppAccordion>

        <editModal.ControlledModal disable-backdrop closeable>
          <EditSeminarSchedule
            v-if="selectedSeminar"
            :date="selectedDate"
            :schedule="selectedSchedule"
            :title="selectedSeminar.title"
            :disabledSubmit="isFetching"
            @create="createSchedule"
            @update="updateSchedule"
            @delete="deleteSchedule"
          />
        </editModal.ControlledModal>
        <deleteSeminarModal.ControlledModal disable-backdrop closeable>
          <DeleteSeminar @delete="deleteSeminar" />
        </deleteSeminarModal.ControlledModal>
      </section>
      <section v-else :class="[$style.section__contents, $style.todo]">
        <CheckCircle :class="$style.todo__icon" />
        <div :class="[$style.todo__contents, $style.todoContents]">
          <p :class="$style.todoContents__todo">
            セミナーが登録されていません。セミナーの申込みをしましょう。
          </p>
          <p :class="$style.todoContents__description">
            低コストかつ完全成果報酬で効率良くユーザーとのアポが獲得できます。
          </p>
        </div>
        <ExternalLink
          :class="[$style.todo__link]"
          href="https://seminarhowto.super.site/"
          noIcon
        >
          セミナーの詳細はこちら
        </ExternalLink>
      </section>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  useSeminarListPageQuery,
  useCreateSeminarSchedulesMutation,
  useUpdateSeminarScheduleMutation,
  useDeleteSeminarScheduleMutation,
  useDeleteSeminarMutation,
} from './SeminarListPage.generated'
import DeleteSeminar from './DeleteSeminar.vue'
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import AppAccordion from '@/components/molecules/AppAccordion.vue'
import SeminarCalendar from './SeminarCalendar.vue'
import CheckCircle from '@/assets/images/icons/check.svg?component'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import EditSeminarSchedule from './EditSeminarSchedule.vue'
import { useModal } from '@/hooks/useModal'
import { useAlert } from '@/libs/alertPortal'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { SHADOW_HOST } from '@/config'

import type { SubmittedForm } from './EditSeminarSchedule.vue'
import type { SeminarListPageQuery } from './SeminarListPage.generated'
import { parseTimeString } from '@/libs/utilities/gql-scalar'

const { result, refetch } = useSeminarListPageQuery()

const seminars = computed(() => {
  return result.value?.seminars || []
})

const route = useRoute('seminarList')
const entranceGUID = computed(() => route.params.entranceGUID)
watch(
  entranceGUID,
  () => {
    refetch()
  },
  { immediate: true }
)

const editModal = useModal()
const deleteSeminarModal = useModal()

type Seminar = SeminarListPageQuery['seminars'][number]

const selectedDate = ref(new Date())
const selectedSeminarId = ref<Seminar['id'] | null>(null)
const selectedScheduleId = ref<Seminar['schedules'][number]['id'] | null>(null)

const selectedSeminar = computed<Seminar | null>(() => {
  return (
    seminars.value?.find(({ id }) => id === selectedSeminarId.value) || null
  )
})

const selectedSchedule = computed<Seminar['schedules'][number] | null>(() => {
  return (
    selectedSeminar.value?.schedules.find(
      ({ id }) => id === selectedScheduleId.value
    ) ?? null
  )
})

const addSchedule = (seminarId: Seminar['id'], date: Date) => {
  selectedDate.value = date
  selectedSeminarId.value = seminarId
  selectedScheduleId.value = null

  editModal.openModal()
}

const editSchedule = (
  seminarId: Seminar['id'],
  scheduleId: Seminar['schedules'][number]['id']
) => {
  selectedSeminarId.value = seminarId
  selectedScheduleId.value = scheduleId

  editModal.openModal()
}

const alert = useAlert()

const { mutate: createSeminarSchedules } = useCreateSeminarSchedulesMutation({})
const { mutate: updateSeminarSchedule } = useUpdateSeminarScheduleMutation({})
const { mutate: deleteSeminarSchedule } = useDeleteSeminarScheduleMutation({})
const { mutate: mutateDeleteSeminar } = useDeleteSeminarMutation({})

const isFetching = ref(false)

const createSchedule = async (form: SubmittedForm) => {
  if (!selectedSeminarId.value) {
    return
  }

  isFetching.value = true

  try {
    await createSeminarSchedules({
      seminarId: selectedSeminarId.value,
      input: [
        {
          startedAt: parseTimeString(form.startedAt),
          endedAt: parseTimeString(form.endedAt),
          location: form.location,
          isOnline: form.isOnline,
          capacity: form.capacity !== '' ? Number(form.capacity) : null,
          applicationDue: parseTimeString(form.applicationDue),
        },
      ],
    })

    alert.success('日程を追加しました。')

    editModal.closeModal()
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  } finally {
    isFetching.value = false
  }
}

const updateSchedule = async (form: SubmittedForm) => {
  if (!selectedSeminarId.value || !selectedScheduleId.value) {
    return
  }

  isFetching.value = true

  try {
    await updateSeminarSchedule({
      seminarId: selectedSeminarId.value,
      scheduleId: selectedScheduleId.value,
      input: {
        startedAt: parseTimeString(form.startedAt),
        endedAt: parseTimeString(form.endedAt),
        location: form.location,
        isOnline: form.isOnline,
        capacity: form.capacity !== '' ? Number(form.capacity) : null,
        applicationDue: parseTimeString(form.applicationDue),
      },
    })

    alert.success('日程を変更しました。')

    editModal.closeModal()
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  } finally {
    isFetching.value = false
  }
}

const deleteSchedule = async () => {
  if (
    !selectedSeminarId.value ||
    !selectedSeminar.value ||
    !selectedScheduleId.value
  ) {
    return
  }

  if (selectedSeminar.value?.schedules.length === 1) {
    // 削除することでセミナーのスケジュールが 0個になる操作なので、セミナーごと削除するよう案内する
    deleteSeminarModal.openModal()
    return
  }

  isFetching.value = true

  try {
    await deleteSeminarSchedule({
      seminarId: selectedSeminarId.value,
      scheduleId: selectedScheduleId.value,
    })

    alert.success('日程を削除しました。')

    editModal.closeModal()
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  } finally {
    isFetching.value = false
  }
}

const deleteSeminar = async () => {
  if (!selectedSeminarId.value) {
    return
  }

  await mutateDeleteSeminar({ seminarId: selectedSeminarId.value })

  refetch()

  alert.success('セミナーの削除が完了しました。')

  deleteSeminarModal.closeModal()
  editModal.closeModal()
}

const buildShadowSeminarURL = (seminarId: Seminar['id']) => {
  return `${SHADOW_HOST}/seminar/${seminarId}`
}
</script>

<style lang="scss" module>
.page {
  &__section {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.header {
  display: flex;
  align-items: center;

  &__title {
    flex: 1;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.section {
  &__contents {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.seminars {
  &__item {
    border-radius: 8px;

    &:nth-child(n + 2) {
      margin-top: 20px;
    }
  }

  &__calendar {
    margin-top: 20px;
  }
}

.link {
  color: $color-text-and-object-accent-primary;

  &:hover {
    color: $color-text-and-object-accent-primary;
  }
}

.contents {
  display: flex;
  overflow: hidden;
  min-height: 88px;
  align-items: center;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-20;

  &__message {
    &--empty {
      color: $color-text-and-object-low-emphasis;
      font-size: 18px;
      font-weight: bold;
      text-align: center;
    }
  }
}

.todo {
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-20;
  display: flex;
  width: 100%;
  padding: 32px;

  &__icon {
    width: 20px;
    height: 20px;
  }

  &__contents {
    flex-grow: 1;
  }

  &__link {
    padding: 12px 0;
    color: $color-text-and-object-accent-primary;

    &:hover {
      color: $color-text-and-object-accent-primary;
    }
  }
}

.todoContents {
  margin-left: 8px;

  :nth-child(n + 2) {
    margin-top: 4px;
  }

  &__todo {
    color: $color-text-and-object-high-emphasis;
    font-size: 16px;
    font-weight: bold;
  }

  &__description {
    color: $color-text-and-object-medium-emphasis;
    font-size: 14px;
  }
}
</style>
